import { useState, type FormEvent } from 'react';

import { LocaleLink as Link } from '../lib/locale-link.js';
import { useMarketingSeo } from '../lib/use-marketing-seo.js';
import { MarketingShell } from '../components/marketing-shell.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrialDays } from '../lib/use-trial-days.js';
import { useI18n } from '../lib/use-i18n.js';
import { callSubmitInquiry } from '../lib/callables/contact.js';
import { logger } from '../lib/logger.js';

/**
 * /contact — three channels surfaced clearly:
 *   - Operator signup (CTA, the most likely visitor intent)
 *   - Direct contact (email + WhatsApp, pulled from platformBrands.contact)
 *   - "Send us a message" form — posts to the `submitInquiry` callable, which
 *     durably stores the message at `contactInquiries/{id}` (rate-limited +
 *     App Check). Full loading/error/success states (no-dead-ends). A
 *     super-admin moderation page reads these later.
 */
export function ContactPage() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  useMarketingSeo({
    title: t('marketing.contact.metaTitle', { brand: brand.name }),
    description: t('marketing.contact.metaDescription', { brand: brand.name }),
  });
  const waNumber = brand.contact.whatsapp ?? '';
  const waLink = waNumber ? `https://wa.me/${waNumber.replace(/[^0-9]/g, '')}` : null;
  return (
    <MarketingShell>
      <Hero />
      <Channels email={brand.contact.email} waLink={waLink} waNumber={waNumber} />
      <InquiryForm />
    </MarketingShell>
  );
}

function Hero() {
  const { t } = useI18n();
  return (
    <section
      style={{
        padding: 'var(--space-12) var(--space-6) var(--space-6) var(--space-6)',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <p style={eyebrow}>{t('marketing.contact.eyebrow')}</p>
        <h1
          style={{
            fontSize: 'var(--text-display)',
            lineHeight: 1.05,
            fontWeight: 700,
            margin: 'var(--space-2) 0 0 0',
            letterSpacing: '-0.02em',
          }}
        >
          {t('marketing.contact.title')}
        </h1>
        <p
          style={{
            margin: 'var(--space-4) auto 0 auto',
            maxWidth: 480,
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('marketing.contact.subtitle')}
        </p>
      </div>
    </section>
  );
}

function Channels({
  email,
  waLink,
  waNumber,
}: {
  readonly email: string;
  readonly waLink: string | null;
  readonly waNumber: string;
}) {
  const { t } = useI18n();
  const trialDays = useTrialDays();
  return (
    <section style={{ padding: 'var(--space-6) var(--space-6) var(--space-12) var(--space-6)' }}>
      <div
        style={{
          maxWidth: 960,
          margin: '0 auto',
          display: 'grid',
          gap: 'var(--space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
        }}
      >
        <Card
          title={t('marketing.contact.channelTrialTitle')}
          body={t('marketing.contact.channelTrialBody', { days: trialDays })}
          ctaHref="/signup"
          ctaLabel={t('marketing.contact.channelTrialCta')}
          internal
        />
        <Card
          title={t('marketing.contact.channelEmailTitle')}
          body={email}
          ctaHref={`mailto:${email}`}
          ctaLabel={t('marketing.contact.channelEmailCta')}
        />
        {waLink ? (
          <Card
            title={t('marketing.contact.channelWhatsappTitle')}
            body={waNumber}
            ctaHref={waLink}
            ctaLabel={t('marketing.contact.channelWhatsappCta')}
            external
          />
        ) : null}
      </div>
    </section>
  );
}

function Card({
  title,
  body,
  ctaHref,
  ctaLabel,
  internal = false,
  external = false,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaHref: string;
  readonly ctaLabel: string;
  readonly internal?: boolean;
  readonly external?: boolean;
}) {
  const sharedCtaStyle: React.CSSProperties = {
    display: 'inline-block',
    marginTop: 'var(--space-4)',
    padding: '8px 16px',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--color-primary)',
    color: 'var(--color-primary-fg)',
    fontSize: 'var(--text-meta)',
    fontWeight: 500,
    textDecoration: 'none',
  };
  return (
    <article
      style={{
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>{title}</h3>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          wordBreak: 'break-word',
        }}
      >
        {body}
      </p>
      {internal ? (
        <Link to={ctaHref} style={sharedCtaStyle}>
          {ctaLabel}
        </Link>
      ) : (
        <a
          href={ctaHref}
          style={sharedCtaStyle}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {ctaLabel}
        </a>
      )}
    </article>
  );
}

type SubmitStatus = 'idle' | 'submitting' | 'done' | 'error';

function InquiryForm() {
  const brand = usePlatformBrand();
  const { locale, t } = useI18n();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  /**
   * Consent for the agent-lead path (G25). Starts FALSE and is never
   * pre-ticked: writing to the company is not the same as agreeing to be called
   * by a commissioned salesperson, and a default-on box is not consent.
   */
  const [wantsRep, setWantsRep] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg(null);
    try {
      await callSubmitInquiry({
        name,
        email,
        message,
        locale,
        platformBrandId: brand.id,
        wantsRepresentativeContact: wantsRep,
      });
      setStatus('done');
    } catch (err) {
      logger.error('submitInquiry failed', { error: String(err) });
      // Surface the server's own message for known, actionable errors (e.g. the
      // rate-limit "give it an hour" copy); otherwise a friendly generic retry.
      const raw = err instanceof Error ? err.message : '';
      const friendly =
        raw && !/internal|unknown|invalid input/i.test(raw)
          ? raw
          : t('marketing.contact.errorGeneric');
      setErrorMsg(friendly);
      setStatus('error');
    }
  }

  if (status === 'done') {
    return (
      <section
        style={{ padding: 'var(--space-12) var(--space-6)', background: 'var(--tint-subtle)' }}
      >
        <div
          style={{
            maxWidth: 480,
            margin: '0 auto',
            textAlign: 'center',
            padding: 'var(--space-8)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-surface)',
            border: '1px solid rgba(13,122,79,0.18)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--text-h2)',
              fontWeight: 600,
              color: 'var(--color-primary)',
            }}
          >
            {t('marketing.contact.successTitle')}
          </h2>
          <p
            style={{
              margin: 'var(--space-3) 0 0 0',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {t('marketing.contact.successBody')}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      style={{ padding: 'var(--space-12) var(--space-6)', background: 'var(--tint-subtle)' }}
    >
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>
          {t('marketing.contact.formTitle')}
        </h2>
        <p
          style={{
            margin: 'var(--space-2) 0 var(--space-6) 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t('marketing.contact.formSubtitle')}
        </p>
        <form onSubmit={onSubmit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <Field
            label={t('marketing.contact.nameLabel')}
            value={name}
            onChange={setName}
            autoComplete="name"
            required
          />
          <Field
            label={t('marketing.contact.emailLabel')}
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
          />
          <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <span
              style={{
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                fontWeight: 500,
              }}
            >
              {t('marketing.contact.messageLabel')}
            </span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={5}
              style={{
                padding: '12px 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border-strong)',
                fontSize: 'var(--text-body)',
                fontFamily: 'inherit',
                background: 'var(--color-surface)',
                color: 'var(--color-on-surface)',
                resize: 'vertical',
              }}
            />
          </label>

          {/* Consent, stated plainly next to the data it releases. */}
          <label
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 'var(--space-3)',
              alignItems: 'start',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={wantsRep}
              onChange={(e) => setWantsRep(e.target.checked)}
              style={{ width: 20, height: 20, marginTop: 2, accentColor: 'var(--color-primary)' }}
            />
            <span style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontWeight: 600 }}>
                {t('marketing.contact.repConsentLabel', { brand: brand.shortName })}
              </span>
              <span
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  lineHeight: 1.5,
                }}
              >
                {t('marketing.contact.repConsentHint', { brand: brand.shortName })}
              </span>
            </span>
          </label>

          {status === 'error' && errorMsg ? (
            <p
              role="alert"
              style={{
                margin: 0,
                padding: 'var(--space-3) var(--space-4)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-tone-warn-bg, rgba(180,83,9,0.08))',
                color: 'var(--color-on-surface)',
                fontSize: 'var(--text-meta)',
                lineHeight: 1.5,
              }}
            >
              {errorMsg}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={status === 'submitting'}
            aria-busy={status === 'submitting'}
            style={{
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              padding: '14px 20px',
              borderRadius: 'var(--radius-md)',
              fontWeight: 500,
              border: 'none',
              cursor: status === 'submitting' ? 'progress' : 'pointer',
              opacity: status === 'submitting' ? 0.7 : 1,
              boxShadow: 'var(--shadow-2)',
            }}
          >
            {status === 'submitting' ? t('marketing.contact.sending') : t('marketing.contact.send')}
          </button>
        </form>
      </div>
    </section>
  );
}

interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly type?: string;
  readonly required?: boolean;
  readonly autoComplete?: string;
}

function Field({ label, value, onChange, type = 'text', required, autoComplete }: FieldProps) {
  return (
    <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required ?? false}
        autoComplete={autoComplete ?? 'off'}
        style={{
          padding: '12px 14px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--color-border-strong)',
          fontSize: 'var(--text-body)',
          fontFamily: 'inherit',
          background: 'var(--color-surface)',
          color: 'var(--color-on-surface)',
        }}
      />
    </label>
  );
}

const eyebrow: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-primary)',
  letterSpacing: 3,
  textTransform: 'uppercase',
  fontWeight: 600,
  margin: 0,
};
